import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  DEV_TOOLS_ENABLED,
  DEV_PASSWORD,
  DEV_USERS,
  DEV_LOGIN_PARAM,
  DEV_LOGOUT_PARAM,
  DEV_CONCEPTS,
  conceptOf,
  conceptLoginHost,
  devLoginUrl,
  loginPageUrl,
} from './devUsers';

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

// DEV-ONLY portal switcher that overlays every app (login + all portals).
// One click logs in as any seeded user on their correct host and lands them in
// their portal. Gated by DEV_TOOLS_ENABLED — inert in production.

function useDevAutoLogin(): string | null {
  const { login, logout, homePath } = useAuth();
  const navigate = useNavigate();
  const [authingAs, setAuthingAs] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    const params = new URLSearchParams(window.location.search);
    const email = params.get(DEV_LOGIN_PARAM);
    const wantsLogout = params.get(DEV_LOGOUT_PARAM);
    if (!email && !wantsLogout) return;
    ran.current = true;

    // Sign out on arrival so the brand's login page renders signed-out. Hard reload
    // to /login (dropping the param): logout() clears the query cache, so a
    // client-side navigate would leave the host's branding unresolved until a manual
    // refresh — a full reload re-fetches the concept-config cleanly on this host.
    if (wantsLogout) {
      void (async () => {
        await logout().catch(() => undefined);
        window.location.replace('/login');
      })();
      return;
    }

    setAuthingAs(email);
    void (async () => {
      try {
        const me = await login({ email: email!, password: DEV_PASSWORD });
        // Hard reload to the landing path: dev-login signs in OVER an active
        // session, so a client-side navigate would keep the previous user's theme
        // (in-flight concept queries from the old session win the race). A full
        // reload re-resolves everything cleanly with the new cookie.
        window.location.replace(homePath(me));
      } catch {
        toast.error(`Dev login failed for ${email}`);
        navigate('/login', { replace: true });
      } finally {
        setAuthingAs(null);
      }
    })();
  }, [login, logout, homePath, navigate]);

  return authingAs;
}

export default function DevTools() {
  const { me, logout } = useAuth();
  const authingAs = useDevAutoLogin();
  const [open, setOpen] = useState(false);

  if (!DEV_TOOLS_ENABLED) return null;

  const overlay = authingAs && (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-white/80 backdrop-blur-sm text-[#001738]">
      <div className="text-center">
        <div className="mx-auto mb-3 h-6 w-6 animate-spin rounded-full border-2 border-[#0043B8]/25 border-t-[#0043B8]" />
        <p className="text-sm font-medium">Signing in as {authingAs}…</p>
      </div>
    </div>
  );

  return (
    <>
      {overlay}

      {/* Edge handle (LKQ blue) */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed right-0 top-1/2 z-[9998] -translate-y-1/2 rounded-l bg-[#0043B8] px-1.5 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-lg [writing-mode:vertical-rl]"
          title="Dev portal switcher"
        >
          Dev
        </button>
      )}

      {/* Blurred backdrop — click to close */}
      {open && (
        <div
          className="fixed inset-0 z-[9998] bg-[#001738]/25 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      {/* Slideout panel — content only mounts when open so the collapsed panel
          never injects text into the DOM / a11y tree (and never pollutes tests). */}
      <div
        className={`fixed right-0 top-0 z-[9999] flex h-full w-[340px] max-w-[88vw] transform flex-col bg-white text-[#001738] shadow-2xl transition-transform duration-200 ${open ? 'translate-x-0' : 'translate-x-full'}`}
        aria-hidden={!open}
      >
        {open && (
        <>
        {/* Header — LKQ branded */}
        <header className="flex items-center justify-between gap-3 bg-[#0043B8] px-4 py-3 text-white">
          <span className="flex items-center gap-2.5">
            <img src="/lkq-logo.svg" alt="LKQ Europe" className="h-[18px] w-auto" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">Dev tools</span>
          </span>
          <button type="button" onClick={() => setOpen(false)} className="-mr-1 rounded p-1 text-white/80 hover:bg-white/15 hover:text-white" aria-label="Close">✕</button>
        </header>

        <div className="flex-1 overflow-y-auto">
        {/* Current session */}
        <section className="border-b border-[#E4E9F0] px-4 py-3.5">
          <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#98A2B3]">Current session</h3>
          {me ? (
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EAEEF3] text-[12px] font-semibold text-[#51586A]">{initials(`${me.user.firstName} ${me.user.lastName}`)}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{me.user.firstName} {me.user.lastName}</div>
                <div className="truncate text-[11px] text-[#687385]">{me.user.email}</div>
              </div>
              <button
                type="button"
                onClick={() => void logout()}
                className="shrink-0 rounded border border-[#E4E9F0] bg-white px-3 py-1.5 text-[12px] font-medium text-[#001738] hover:bg-[#F4F7FA]"
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[12px] text-[#687385]">
              <span className="h-2 w-2 rounded-full bg-[#C3CAD6]" aria-hidden />
              Signed out
              <span className="ml-auto font-mono text-[11px] text-[#98A2B3]">{window.location.hostname}</span>
            </div>
          )}
        </section>

        {/* Sign in as */}
        <section className="px-4 py-3.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#98A2B3]">Sign in as</h3>
          <p className="mb-3 mt-1 text-[11px] leading-relaxed text-[#687385]">
            Tap a user to sign in instantly with the demo password (<span className="font-mono">{DEV_PASSWORD}</span>) — or open a brand’s signed-out login page. Dev/staging only.
          </p>
          <div className="space-y-3">
            {DEV_CONCEPTS.map((conceptKey) => {
              const concept = conceptOf(conceptKey);
              const users = DEV_USERS.filter((u) => u.concept === conceptKey);
              const loginHost = conceptLoginHost(conceptKey);
              return (
                <div key={conceptKey} className="overflow-hidden rounded border border-[#E4E9F0]">
                  {/* Section header: brand + its login page link (top of the section) */}
                  <div className="flex items-center justify-between gap-2 border-b border-[#E4E9F0] bg-[#F8FAFC] px-3 py-2">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: concept.color }} aria-hidden />
                      <span className="text-[11px] font-bold uppercase tracking-[0.06em]" style={{ color: concept.color }}>{concept.label}</span>
                    </span>
                    <a
                      href={loginPageUrl(loginHost)}
                      className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#0043B8] hover:underline"
                    >
                      <span aria-hidden>↗</span> Login page
                    </a>
                  </div>
                  {/* Users below */}
                  <div className="space-y-0.5 p-1.5">
                    {users.map((u) => {
                      const isCurrent = me?.user.email === u.email;
                      return (
                        <a
                          key={u.email}
                          href={devLoginUrl(u)}
                          className={`flex items-center justify-between gap-2 rounded px-2.5 py-2 text-[13px] transition-colors ${isCurrent ? 'bg-[#EFF4FE] font-semibold text-[#0043B8] ring-1 ring-[#0043B8]/30' : 'font-medium text-[#001738] hover:bg-[#F4F7FA]'}`}
                        >
                          <span className="truncate">{u.role}</span>
                          {isCurrent ? (
                            <span className="shrink-0 rounded-sm bg-[#0043B8]/10 px-1 text-[9px] font-bold uppercase tracking-wide text-[#0043B8]">Current</span>
                          ) : (
                            <span className="shrink-0 text-[#C3CAD6]" aria-hidden>→</span>
                          )}
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        </div>
        </>
        )}
      </div>
    </>
  );
}
